import { AccordionContainer, Accordion, BreadcrumbTemplateEnum, breadcrumbTemplates, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderLeft, PageInner, PageSection } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { Component, JSX } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { ResourceType } from "../resources";
import { RoleRestricted, Roles } from "../user";
import { MemberSetTypeEnum } from "./enumerations";
import MemberSetIconBox from "./MemberSetIconBox";
import { MemberSetModel } from "./memberSetModels";
import MemberSetService from "./memberSetService";
import { QesIcons } from "@app/shared/icons/qesIcons";

interface MemberSetsState {
  archivedMemberSets: MemberSetTableData[];
  hasFetchedMemberSets: boolean;
  memberSets: MemberSetTableData[];
  sharedMemberSets: MemberSetTableData[];
  systemMemberSets: MemberSetTableData[];
}

interface MemberSetTableData {
  [wafflesKeyColumnName]: string;
  name: JSX.Element;
  description: string;
  lastUpdated: string;
  lastActivation: string;
  status: string;
  type: string;
  isFiltered: boolean;
}

const membersetColumnConfig: ColumnConfig = {
  description: {
    displayText: "Description",
    testId: "description",
  },
  lastActivation: {
    displayText: "Last Activation",
    testId: "lastActivation",
  },
  lastUpdated: {
    displayText: "Last Updated",
    testId: "lastUpdate",
  },
  name: {
    displayText: "Name",
    testId: "name",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
  type: {
    displayText: "Type",
    testId: "type",
  },
};

export default class MemberSets extends Component<RouteComponentProps<unknown>, MemberSetsState, MemberSetTableData> {

  private readonly memberSetService = new MemberSetService();

  constructor(props: RouteComponentProps<unknown>) {
    super(props);

    this.state = {
      archivedMemberSets: [],
      hasFetchedMemberSets: false,
      memberSets: [],
      sharedMemberSets: [],
      systemMemberSets: [],
    };
  }

  public async componentDidMount() {
    await this.fetchMemberSets();
  }

  public render() {
    let myMemberSetsBody;
    let sharedMemberSetsBody;
    let systemMemberSetsBody;
    let archivedMemberSetsBody;

    if ((this.state.memberSets && this.state.memberSets.length > 0) || !this.state.hasFetchedMemberSets) {
      myMemberSetsBody = (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={this.customSort}
          data={this.state.memberSets}
          fullWidth={true}
          loading={!this.state.hasFetchedMemberSets}
          testId="my-member-set-waffles-table"
        />
      );
    } else {
      myMemberSetsBody = (
        <p className="lead">There are no member populations.</p>
      );
    }

    if ((this.state.sharedMemberSets && this.state.sharedMemberSets.length > 0) || !this.state.hasFetchedMemberSets) {
      sharedMemberSetsBody = (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={this.customSort}
          data={this.state.sharedMemberSets}
          fullWidth={true}
          loading={!this.state.hasFetchedMemberSets}
          testId="shared-member-set-waffles-table"
        />);
    } else {
      sharedMemberSetsBody = (
        <p className="lead">There are no shared member populations.</p>
      );
    }

    if ((this.state.systemMemberSets && this.state.systemMemberSets.length > 0) || !this.state.hasFetchedMemberSets) {
      systemMemberSetsBody = (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={this.customSort}
          data={this.state.systemMemberSets}
          fullWidth={true}
          loading={!this.state.hasFetchedMemberSets}
          testId="system-member-set-waffles-table"
        />);
    } else {
      systemMemberSetsBody = (
        <p className="lead">There are no system member populations.</p>
      );
    }

    if ((this.state.archivedMemberSets && this.state.archivedMemberSets.length > 0) || !this.state.hasFetchedMemberSets) {
      archivedMemberSetsBody = (
        <Waffles<MemberSetTableData>
          columns={membersetColumnConfig}
          customSort={this.customSort}
          data={this.state.archivedMemberSets}
          fullWidth={true}
          loading={!this.state.hasFetchedMemberSets}
          testId="archived-member-set-waffles-table"
        />);
    } else {
      archivedMemberSetsBody = (
        <p className="lead">There are no archived member populations.</p>
      );
    }

    const breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.MemberPopulations];

    return (
      <>
        {(
          <Page documentTitle="Member Populations">
            <Breadcrumbs items={breadcrumbs} />
            <PageInner>
              {/*TODO: Implement links on this page*/}
              <PageHeader title="Member Populations" iconName={QesIcons.FontAwesome.User}>
                <PageHeaderLeft>
                  <div className="ml-2">
                    <i className="fa fa-share text-muted" />
                    <Link
                      to={{
                        pathname: `/resources/share`,
                        state: {
                          breadcrumbItems: breadcrumbs,
                          isSingleResource: false,
                          resourceDescription: "Member Population",
                          resourceTypes: [ResourceType.MemberSet],
                        },
                      }}
                    >
                      Share
                    </Link>
                  </div>
                </PageHeaderLeft>
                {/* <PageHeaderRight>
                  <div className="ml-auto">
                    <div className="btn-group">
                      <div className="btn-group pull-right">
                        <Link to={`${this.props.match.url}/create`}
                        className="qes-button qes-button-primary mt-1 mr5" type="button"><i className="fa fa-plus pr-3" />Add Member Population</Link>
                      </div>
                    </div>
                  </div>
                </PageHeaderRight>
                */}
              </PageHeader>
              <PageSection>
                <div className="row mt-3">
                  <div className="col-12">
                    {this.state.hasFetchedMemberSets ? (
                      <AccordionContainer>
                        {this.state.memberSets.length > 0 && (
                          <Accordion title="My Member Populations" defaultOpen={true} testId="member-sets__my-member-populations-accordion">
                            {myMemberSetsBody}
                          </Accordion>
                        )}
                        <Accordion title="Shared Member Populations" defaultOpen={this.state.memberSets.length === 0} testId="member-sets__shared-member-populations-accordion">
                          {sharedMemberSetsBody}
                        </Accordion>
                        <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                          <Accordion title="System Member Populations" testId="member-sets__system-member-populations-accordion">
                            {systemMemberSetsBody}
                          </Accordion>
                        </RoleRestricted>
                        {this.state.archivedMemberSets.length > 0 && (
                          <Accordion title="Archived Member Populations" testId="member-sets__archived-member-populations-accordion">
                            {archivedMemberSetsBody}
                          </Accordion>
                        )}
                      </AccordionContainer>
                    ) : <LoadingSpinner />
                    }
                  </div>
                </div>
              </PageSection>
            </PageInner>
          </Page>
        )}
      </>
    );
  }

  private fetchMemberSets = async (): Promise<void> => {
    const memberSets = await this.memberSetService.getOnRole(true, true, false, true);
    this.fetchMemberSetsResponse(memberSets);
  }

  private fetchMemberSetsResponse = (response: MemberSetModel[]): void => {
    const sets: MemberSetModel[] = [];
    const sharedSets: MemberSetModel[] = [];
    const systemSets: MemberSetModel[] = [];
    const archivedSets: MemberSetModel[] = [];

    //add a check for archived sets
    response.forEach((ms: MemberSetModel) => {
      if (!ms.resource.isArchived && !ms.resource.isHidden) {
        if (ms.resource.isSystemResource) {
          systemSets.push(ms);
        } else if (!ms.resource.isShared) {
          sets.push(ms);
        } else {
          sharedSets.push(ms);
        }
      } else {
        archivedSets.push(ms);
      }
    });

    sets.sort((a) => a.sourceMemberSetId ? 1 : -1);
    sharedSets.sort((a) => a.sourceMemberSetId ? 1 : -1);
    archivedSets.sort((a) => a.sourceMemberSetId ? 1 : -1);

    this.setState(() => ({
      archivedMemberSets: this.createWafflesTable(archivedSets),
      hasFetchedMemberSets: true,
      memberSets: this.createWafflesTable(sets),
      sharedMemberSets: this.createWafflesTable(sharedSets),
      systemMemberSets: this.createWafflesTable(systemSets),
    }));
  }

  private handleMemberPopulationSelected(memberSetId: number): void {
    this.props.history.push(`${this.props.match.url}/${memberSetId}/detail`);
  }

  private createWafflesTable(data: MemberSetModel[]) {
    /* eslint-disable sort-keys */
    return data.map((rec) => {
      const sortedVersions = [...rec.memberSetVersions].sort((a, b) => new Date(a.dateCreated) < new Date(b.dateCreated) ? 1 : -1);
      const latestVersion = sortedVersions[0];
      const lastActivatedVersion = (rec.memberSetVersions.filter((a) => a.dateActivated).length > 0 ? rec.memberSetVersions.sort((a, b) => new Date(a.dateActivated) > new Date(b.dateActivated) ? 1 : -1).map((value) => value.memberSetVersionId).pop() : 0); //0 if no activated versions

      let status = "";
      if (rec.memberSetVersions.some((x) => x.dateActivated == null)) {
        if (rec.memberSetVersions.some((x) => x.dateActivated != null)) {
          if (rec.memberSetVersions.some((x) => x.dateActivated == null && x.memberSetVersionId > lastActivatedVersion)) {
            status = "New Version Pending";
          } else {
            //this is a memberset who has a non activated version but that version is not the latest version? what should the status be? In 1.0 it is left blank
          }
        } else {
          status = "Publish Pending";
        }
      } else {
        status = "Published";
      }

      const name = (
        <div key={rec.resource.name} onClick={() => this.handleMemberPopulationSelected(rec.memberSetId)}>
          {MemberSetIconBox(rec)}
        </div>
      );
      return ({
        key: rec.memberSetId.toString(),
        name,
        description: rec.resource.description,
        lastUpdated: new Date(latestVersion.dateLastUpdated).toLocaleString("en-US"),
        lastActivation: latestVersion.dateActivated ? new Date(latestVersion.dateActivated).toLocaleString("en-US") : "",
        status,
        type: rec.memberSetType === MemberSetTypeEnum.Full ? "Listing" : rec.memberSetType,
        isFiltered: rec.sourceMemberSetId ? true : false,
      });
      /* eslint-enable sort-keys */
    });
  }

  private customSort = (records: MemberSetTableData[], column: keyof MemberSetTableData, direction: string) => {
    const data = [...records];
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    const filtered = data.filter((a) => a.isFiltered);
    const notFiltered = data.filter((a) => !a.isFiltered);

    switch (column) {
      case "key":
      case "description":
        return notFiltered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier)
          .concat(filtered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier));
      case "name":
        return notFiltered.sort(
          (a, b) => {
            return ((a[column].key as string).localeCompare(b[column].key as string, "en", { sensitivity: "base" })) * multiplier;
          }).concat(filtered.sort(
            (a, b) => {
              return ((a[column].key as string).localeCompare(b[column].key as string, "en", { sensitivity: "base" })) * multiplier;
            }));
      case "lastUpdated":
        return notFiltered.sort((a, b) => (new Date(a.lastUpdated) > new Date(b.lastUpdated) ? 1 : -1) * multiplier)
          .concat(filtered.sort((a, b) => (new Date(a.lastUpdated) > new Date(b.lastUpdated) ? 1 : -1) * multiplier));
      case "lastActivation":
        return notFiltered.sort((a, b) => (new Date(a.lastActivation) > new Date(b.lastActivation) ? 1 : -1) * multiplier)
          .concat(filtered.sort((a, b) => (new Date(a.lastActivation) > new Date(b.lastActivation) ? 1 : -1) * multiplier));
      case "status":
        return notFiltered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier)
          .concat(filtered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier));
      case "type":
        return notFiltered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier)
          .concat(filtered.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier));
      default:
        console.warn(`No sort definition for column ${column}.`);
        return records;
    }
  }
}
